/*
 *
 * PaymentMethods reducer
 *
 */
import produce from 'immer';
import { titleCase } from 'utils/common';
import {
  DEFAULT_ACTION,
  ENABLE_LOADER,
  DISABLE_LOADER,
  HANDLE_PAYMENT_METHODS_RESPONSE,
  HANDLE_OTHER_AVAILABLE_MODES_RESPONSE,
  ZEST_MONEY_CODE,
  INSTA_CRED_CODE,
  ENABLED_PAYMENT_METHODS,
  PAYMENT_METHODS,
  HANDLE_PAYPAL_CONFIG_RESPONSE,
  RESET_PAYMENT_METHOD_CARD_LOADER,
  HANDLE_UPDATE_DEFAULT_PAYPAL_CURRENCY,
  HANDLE_DEACTIVATE_PAYPAL_CURRENCY,
  HANDLE_ACTIVATE_PAYPAL_CURRENCY,
  RESET_DATA,
  PAYPAL,
  PAYTM,
  SAVE_PAYTM_DATA,
  SAVE_PAYTM_PROD_CONFIG,
  SAVE_PAYTM_TEST_CONFIG,
  IN_REVIEW,
  REJECTED,
  CARDLESS_EMI,
  ENABLE_PAYMENT_METHODS_CODES,
} from './constants';

const getInitialState = () => ({
  isLoading: false,
  data: {},
  enabledModes: [],
  allModesData: {
    DEBIT_CARD: {
      enabled: [],
      otherAvailable: [],
      inReview: [],
    },
    CREDIT_CARD: {
      enabled: [],
      otherAvailable: [],
      inReview: [],
    },
    PREPAID_CARD: {
      enabled: [],
      otherAvailable: [],
      inReview: [],
    },
    CORPORATE_CREDIT_CARD: {
      enabled: [],
      otherAvailable: [],
      inReview: [],
    },
    NET_BANKING: {
      enabled: [],
      otherAvailable: [],
      inReview: [],
    },
    UPI: {
      enabled: [],
      otherAvailable: [],
      inReview: [],
    },
    Wallet: {
      enabled: [],
      otherAvailable: [],
      inReview: [],
    },
    CARDLESS_EMI: {
      enabled: [],
      otherAvailable: [],
      inReview: [],
    },
    CREDIT_CARD_EMI: {
      enabled: [],
      otherAvailable: [],
      inReview: [],
    },
    PAY_LATER: {
      enabled: [],
      otherAvailable: [],
      inReview: [],
    },
  },
  paymentMethods: Object.keys(PAYMENT_METHODS).map(key => ({
    key,
    displayText: PAYMENT_METHODS[key],
    activeModes: [],
    inActiveModes: [],
  })),
  otherModesdata: [],
  paypalAccount: {},
  defaultPaypalCurrency: '',
  activePaypalCurrencies: [],
  paypalCurrencies: [],
  paytmProdConfig: {},
  paytmTestConfig: {},
  paymentMethodCardLoader: {
    [PAYPAL]: false,
    [PAYTM]: false,
  },
  paymentMethodsByStatus: {
    [IN_REVIEW]: {},
    [REJECTED]: {},
  },
  enablePaymentMethodsData: {},
});

export const initialState = {
  ...getInitialState(),
};

/* eslint-disable default-case, no-param-reassign */
const paymentMethodsReducer = (state = initialState, action) =>
  produce(state, draft => {
    switch (action.type) {
      case ENABLE_LOADER: {
        draft.isLoading = true;
        break;
      }
      case DISABLE_LOADER: {
        draft.isLoading = false;
        break;
      }
      case HANDLE_PAYMENT_METHODS_RESPONSE: {
        const { enabledModes, paymentModes } = action.data;

        draft.enabledModes = enabledModes;

        if (paymentModes.CREDIT_CARD_EMI) {
          const cardlessEMI = paymentModes.CREDIT_CARD_EMI.filter(obj =>
            [ZEST_MONEY_CODE, INSTA_CRED_CODE].includes(obj.code),
          );

          if (!paymentModes.CARDLESS_EMI) paymentModes.CARDLESS_EMI = [];
          else
            paymentModes.CARDLESS_EMI = paymentModes.CARDLESS_EMI.concat(
              cardlessEMI,
            );

          paymentModes.CREDIT_CARD_EMI = paymentModes.CREDIT_CARD_EMI.filter(
            obj => ![ZEST_MONEY_CODE, INSTA_CRED_CODE].includes(obj.code),
          );
        }

        const filteredPaymentModes = Object.keys(paymentModes)
          .filter(key => ENABLED_PAYMENT_METHODS.includes(key))
          .reduce((obj, key) => {
            obj[key] = paymentModes[key];
            return obj;
          }, {});

        Object.keys(filteredPaymentModes).forEach(key => {
          draft.allModesData[key].enabled = filteredPaymentModes[key];
        });
        break;
      }
      case HANDLE_OTHER_AVAILABLE_MODES_RESPONSE: {
        const [otherAvailableModes, modesByStatus] = action.data;
        // computing the modes by status
        const inReviewAndRejectedModes = {};

        const enablePaymentMethodCodes = modesByStatus.hideEnablePaymentMethods
          ? {}
          : { ...ENABLE_PAYMENT_METHODS_CODES };

        const inReviewModes = {};
        const rejectedModes = {};

        modesByStatus.paymentModes.forEach(obj => {
          const { code, status, paymentGroup } = obj;
          if (status === IN_REVIEW) {
            inReviewAndRejectedModes[code] = true;
            // remove the payment method from the self-onboarding group if it's IN_REVIEW
            if (code in enablePaymentMethodCodes)
              delete enablePaymentMethodCodes[code];
            if (paymentGroup in inReviewModes) {
              inReviewModes[paymentGroup].push(obj);
            } else {
              inReviewModes[paymentGroup] = [obj];
            }
          } else if (status === REJECTED) {
            inReviewAndRejectedModes[code] = true;
            if (paymentGroup in rejectedModes) {
              rejectedModes[paymentGroup].push(obj);
            } else {
              rejectedModes[paymentGroup] = [obj];
            }
          }
        });

        draft.paymentMethodsByStatus[IN_REVIEW] = inReviewModes;
        draft.paymentMethodsByStatus[REJECTED] = rejectedModes;

        const otherAvailableModesData = otherAvailableModes.paymentModes.map(
          mode =>
            mode.code === ZEST_MONEY_CODE || mode.code === INSTA_CRED_CODE
              ? { ...mode, paymentGroup: CARDLESS_EMI }
              : mode,
        );

        const filteredPaymentModes = otherAvailableModesData.filter(mode =>
          ENABLED_PAYMENT_METHODS.includes(mode.paymentGroup),
        );

        const enablePaymentMethodsData = {};

        Object.keys(draft.allModesData).forEach(key => {
          const otherAvailable = [];

          filteredPaymentModes.forEach(mode => {
            if (
              mode.paymentGroup === key &&
              !inReviewAndRejectedModes[mode.code]
            ) {
              if (
                draft.allModesData[key].enabled.every(d => d.code !== mode.code)
              ) {
                otherAvailable.push(mode);
                if (mode.code in enablePaymentMethodCodes) {
                  if (!enablePaymentMethodsData[mode.paymentGroup]) {
                    enablePaymentMethodsData[mode.paymentGroup] = {
                      displayText: titleCase(mode.paymentGroup),
                      paymentModes: [mode],
                    };
                  } else {
                    enablePaymentMethodsData[
                      mode.paymentGroup
                    ].paymentModes.push(mode);
                  }
                }
              }
            }
          });

          draft.allModesData[key].otherAvailable = otherAvailable;

          if (key in inReviewModes)
            draft.allModesData[key].inReview = inReviewModes[key];
        });
        draft.enablePaymentMethodsData = enablePaymentMethodsData;
        draft.isLoading = false;
        break;
      }
      case HANDLE_PAYPAL_CONFIG_RESPONSE: {
        const {
          activeAccount,
          defaultCurrency,
          activeCurrencies,
          paypalCurrencies,
        } = action.data;

        if (activeAccount) {
          draft.paypalAccount = activeAccount;

          if (defaultCurrency && defaultCurrency.currencyCode)
            draft.defaultPaypalCurrency = defaultCurrency.currencyCode;

          if (activeCurrencies && activeCurrencies.length > 0)
            draft.activePaypalCurrencies = activeCurrencies
              .map(({ isActive, currencyCode }) =>
                isActive ? currencyCode : '',
              )
              .filter(obj => !!obj);

          if (paypalCurrencies && paypalCurrencies.length > 0)
            draft.paypalCurrencies = paypalCurrencies
              .map(({ currencyCode, currency }) => ({
                key: currencyCode,
                value: currencyCode,
                text: currency,
              }))
              .filter(obj => !!obj);
        } else {
          draft.paypalAccount = {};
        }
        draft.paymentMethodCardLoader[PAYPAL] = false;

        break;
      }
      case RESET_PAYMENT_METHOD_CARD_LOADER: {
        draft.paymentMethodCardLoader[action.data] = !draft
          .paymentMethodCardLoader[action.data];
        break;
      }
      case HANDLE_UPDATE_DEFAULT_PAYPAL_CURRENCY: {
        draft.defaultPaypalCurrency = action.data.currencyCode;
        break;
      }
      case HANDLE_DEACTIVATE_PAYPAL_CURRENCY: {
        const { currencyCode } = action.data;
        draft.activePaypalCurrencies = draft.activePaypalCurrencies.filter(
          elem => elem !== currencyCode,
        );
        draft.isLoading = false;
        break;
      }
      case HANDLE_ACTIVATE_PAYPAL_CURRENCY: {
        const { currencyCode } = action.data;
        if (!draft.activePaypalCurrencies.includes(currencyCode))
          draft.activePaypalCurrencies.push(currencyCode);
        break;
      }
      case SAVE_PAYTM_DATA: {
        const { paytmProdConfig, paytmTestConfig } = action.data;
        draft.paytmProdConfig = paytmProdConfig;
        draft.paytmTestConfig = paytmTestConfig;
        draft.paymentMethodCardLoader[PAYTM] = false;
        break;
      }
      case SAVE_PAYTM_PROD_CONFIG: {
        draft.paytmProdConfig = action.data;
        break;
      }
      case SAVE_PAYTM_TEST_CONFIG: {
        draft.paytmTestConfig = action.data;
        break;
      }
      case RESET_DATA: {
        const obj = getInitialState();
        Object.keys(obj).forEach(k => (draft[k] = obj[k])); // eslint-disable-line
        break;
      }
      case DEFAULT_ACTION:
        break;
    }
  });

export default paymentMethodsReducer;
